<template>
  <div class="card mb-3">
    <div class="card-body cg-tight">
      <div
        class="d-flex justify-content-between align-items-baseline flex-wrap gap-2"
      >
        <h3
          class="cg-card-title cg-clickable cg-link"
          @click="$emit('open', row.key)"
        >
          {{ row.name }}
        </h3>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary ms-auto text-nowrap"
          @click="$emit('open', row.key)"
        >
          Open domain
        </button>
      </div>

      <div class="cg-halves">
        <div class="cg-half cg-rule-top">
          <div
            class="d-flex justify-content-between align-items-baseline gap-2 mb-2"
          >
            <p class="cg-micro mb-0">Consumption data · {{ row.dataScope }}</p>
            <ToneBadge
              v-if="row.dataVerdict"
              :tone="row.dataTone"
              :label="row.dataVerdict"
            />
          </div>
          <StatBlock :value="fmt(row.fy)" unit="MWh" class="mb-2" />
          <MonthStrip :cells="row.monthCells" />
          <p class="mb-0 mt-1 text-body-secondary cg-tabular small">
            {{ fmt(row.reported) }} reported ·
            {{ fmt(row.estimated) }} estimated
          </p>
        </div>

        <div class="cg-half cg-rule-top">
          <div
            class="d-flex justify-content-between align-items-baseline gap-2 mb-2"
          >
            <p class="cg-micro mb-0">
              Certificate cover · {{ row.coverScope }}
            </p>
            <ToneBadge
              v-if="row.coverVerdict"
              tone="warn"
              :label="row.coverVerdict"
            />
          </div>
          <StatBlock
            :value="fmt(row.earmarkedTotal)"
            unit="MWh earmarked"
            class="mb-2"
          />
          <MonthStrip v-if="row.coverCells" :cells="row.coverCells" />
          <MonthStrip v-else :cells="[poolCell]" />
          <p class="mb-0 mt-1 text-body-secondary cg-tabular small">
            {{ row.coverDetail }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { DomainKey } from "@/compliance_groups/fixture/complianceGroupsTypes";
import type { ClaimRow, StripCell } from "@/compliance_groups/fixture/derive";
import { fmt } from "@/compliance_groups/fixture/format";
import { computed } from "vue";
import MonthStrip from "./MonthStrip.vue";
import StatBlock from "./StatBlock.vue";
import ToneBadge from "./ToneBadge.vue";

const props = defineProps<{ row: ClaimRow }>();
defineEmits<{ open: [key: DomainKey] }>();

// An annual domain is one pool: a single hoverable cell whose fill is the dominant segment
// (cancelled > in account > expected); the popover carries the full split.
const poolCell = computed<StripCell>(() => {
  const dominant = [...props.row.poolSegments].sort(
    (a, b) => parseFloat(b.pct) - parseFloat(a.pct)
  )[0];
  return {
    label: "2026",
    period: "2026 annual pool",
    fill: dominant?.fill ?? "empty",
    statusLabel: props.row.coverVerdict ?? "Projected covered",
    tone: props.row.coverVerdict ? "warn" : "ok",
    detail: props.row.poolDetail,
  };
});
</script>

<style scoped lang="scss">
.cg-half {
  flex: 1 1 220px;
  min-width: 0;
}
.cg-link:hover {
  text-decoration: underline;
}
</style>
